import React, { useCallback, useEffect, useState } from 'react';

import {
  getWorkspaceAccess,
  grantWorkspacePlan,
  revokeWorkspacePlanGrant,
  editionDisplayName,
  setFreePeriodEnd,
  type Edition,
  type WorkspaceAccess as Access,
} from './api';
import { onlyDate } from './money';

/** A date input's value as the end of that day, UTC. */
const endOfDay = (date: string) => new Date(`${date}T23:59:59Z`).toISOString();

/**
 * What lets this workspace work: a paid plan, a plan given without payment (a
 * grant), or its free period -- and the two levers an admin has. A grant is full
 * access on that plan until it ends (or forever) and holds the plan against
 * billing events; when it ends, a workspace that pays moves to the plan it pays
 * for, one that does not keeps the plan and is asked to pay for it.
 */
export default function WorkspaceAccess({
  accountId,
  editions,
  toast,
  onChanged,
}: {
  accountId: string;
  editions: Edition[];
  toast: (message: string) => void;
  onChanged: () => void;
}) {
  const [access, setAccess] = useState<Access | null>(null);
  const [plan, setPlan] = useState('');
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState('');
  const [freeUntil, setFreeUntil] = useState('');
  const [freeReason, setFreeReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setAccess(await getWorkspaceAccess(accountId));
    } catch (e) {
      toast(`Could not read this workspace's access: ${(e as Error).message}`);
    }
  }, [accountId, toast]);

  useEffect(() => {
    setAccess(null);
    if (accountId) void load();
  }, [accountId, load]);

  const act = async (done: string, fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
      await load();
      onChanged();
      toast(done);
    } catch (e) {
      toast(`Refused: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const paidPlans = editions.filter((e) => e.plan_key !== 'free');
  const name = (key: string) => editionDisplayName(editions.find((e) => e.plan_key === key), key);
  // Developer (tracker 47) has no end date: it ends by moving the workspace to a sold plan.
  const planIsInternal = editions.some((e) => e.plan_key === plan && e.is_internal);
  const live = access?.grants.find((g) => g.live) ?? null;

  return (
    <div className="card" style={{ marginBottom: 18 }}>
      <h2 style={{ marginTop: 0 }}>Access without payment</h2>
      <p className="sub">
        Give this workspace a plan without payment, for a set time or with no end, or move the end of its free period.
        Every change is recorded with its reason.
      </p>

      {!access ? (
        <p className="muted">Loading…</p>
      ) : (
        <>
          <div className="legend">
            <span>Access: <strong>{access.access_state ?? '—'}</strong></span>
            <span>Pays: <strong>{access.paid ? `yes (${access.billing_provider})` : 'no'}</strong></span>
            <span>
              Given plan:{' '}
              <strong>{live ? `${name(live.plan_key)}, ${live.ends_at ? `until ${onlyDate(live.ends_at)}` : 'no end'}` : 'none'}</strong>
            </span>
            <span>
              Free period ends:{' '}
              <strong>{access.free_period_ends_at ? onlyDate(access.free_period_ends_at) : '—'}</strong>
              {access.free_period_fixed ? <span className="muted"> (set)</span> : null}
            </span>
          </div>

          <div className="row" style={{ alignItems: 'end', marginTop: 14, flexWrap: 'wrap', gap: 12 }}>
            <div style={{ flex: '0 1 200px' }}>
              <label>Give plan</label>
              <select value={plan} disabled={busy} onChange={(e) => setPlan(e.target.value)}>
                <option value="">Choose…</option>
                {paidPlans.map((e) => (
                  <option key={e.plan_key} value={e.plan_key}>{editionDisplayName(e)}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: '0 1 180px' }}>
              <label>{planIsInternal ? 'No end date on Developer' : 'Until (empty = no end)'}</label>
              <input type="date" value={planIsInternal ? '' : until} disabled={busy || planIsInternal} onChange={(e) => setUntil(e.target.value)} />
            </div>
            <div style={{ flex: '1 1 240px' }}>
              <label>Why</label>
              <input value={reason} disabled={busy} placeholder="e.g. Founder test workspace" onChange={(e) => setReason(e.target.value)} />
            </div>
            <button
              className="btn"
              disabled={busy || !plan || reason.trim().length < 3}
              onClick={() =>
                void act(`${name(plan)} given${until ? ` until ${until}` : ' with no end'}.`, async () => {
                  await grantWorkspacePlan(accountId, plan, until && !planIsInternal ? endOfDay(until) : null, reason.trim());
                  setPlan('');
                  setUntil('');
                  setReason('');
                })
              }
            >
              {live ? 'Replace given plan' : 'Give plan'}
            </button>
            {live ? (
              <button
                className="btn"
                disabled={busy}
                onClick={() => {
                  const why = window.prompt(`End ${name(live.plan_key)} for this workspace now? Say why.`);
                  if (why && why.trim().length >= 3) {
                    void act('Given plan ended.', () => revokeWorkspacePlanGrant(live.id, why.trim()));
                  }
                }}
              >
                End given plan
              </button>
            ) : null}
          </div>

          <div className="row" style={{ alignItems: 'end', marginTop: 14, flexWrap: 'wrap', gap: 12 }}>
            <div style={{ flex: '0 1 180px' }}>
              <label>Free period ends on</label>
              <input type="date" value={freeUntil} disabled={busy} onChange={(e) => setFreeUntil(e.target.value)} />
            </div>
            <div style={{ flex: '1 1 240px' }}>
              <label>Why</label>
              <input value={freeReason} disabled={busy} placeholder="e.g. Extended while they evaluate" onChange={(e) => setFreeReason(e.target.value)} />
            </div>
            <button
              className="btn"
              disabled={busy || !freeUntil || freeReason.trim().length < 3}
              onClick={() =>
                void act(`Free period now ends ${freeUntil}.`, async () => {
                  await setFreePeriodEnd(accountId, endOfDay(freeUntil), freeReason.trim());
                  setFreeUntil('');
                  setFreeReason('');
                })
              }
            >
              Set free period end
            </button>
          </div>

          {access.grants.length ? (
            <div className="matrix-scroll" style={{ marginTop: 14 }}>
              <table className="matrix">
                <thead>
                  <tr><th>Plan</th><th>From</th><th>Until</th><th>Why</th><th>By</th><th>Ended</th></tr>
                </thead>
                <tbody>
                  {access.grants.map((g) => (
                    <tr key={g.id}>
                      <td className="feat-name">
                        {name(g.plan_key)} {g.live ? <span className="pill">live</span> : null}
                      </td>
                      <td>{onlyDate(g.starts_at)}</td>
                      <td>{g.ends_at ? onlyDate(g.ends_at) : 'no end'}</td>
                      <td>{g.reason}</td>
                      <td>{g.granted_by ?? '—'}</td>
                      <td>{g.revoked_at ? `${onlyDate(g.revoked_at)} · ${g.revoked_reason ?? ''}` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
